import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Alert from '../../components/ui/Alert';
import Loading from '../../components/ui/Loading';
import Badge from '../../components/ui/Badge';
import { 
  Plus, 
  ParkingCircle, 
  Edit2, 
  Trash2, 
  MapPin, 
  Car,
  Truck,
  RefreshCw
} from 'lucide-react';
import { parkingService } from '../../services';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import type { ParkingLot, ParkingSpot } from '../../types';

const ParkingLotsPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId, locations } = useLocationContext();
  
  const selectedLocation = selectedLocationId === 'all' ? '' : selectedLocationId;
  const parkingRequestRef = useRef(0);
  const selectedLocationRef = useRef(selectedLocation);
  selectedLocationRef.current = selectedLocation;
  const [parkingLots, setParkingLots] = useState<ParkingLot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Create lot modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLotName, setNewLotName] = useState('');
  const [newLotCode, setNewLotCode] = useState('');
  const [newLotType, setNewLotType] = useState('general');
  const [creatingLot, setCreatingLot] = useState(false);
  
  // Manage spots modal
  const [showSpotsModal, setShowSpotsModal] = useState(false);
  const [selectedLot, setSelectedLot] = useState<ParkingLot | null>(null);
  const [spots, setSpots] = useState<ParkingSpot[]>([]);
  const [loadingSpots, setLoadingSpots] = useState(false);
  
  // Add spots form
  const [showAddSpotsForm, setShowAddSpotsForm] = useState(false);
  const [spotPrefix, setSpotPrefix] = useState('');
  const [spotStartNumber, setSpotStartNumber] = useState('1');
  const [spotCount, setSpotCount] = useState('10');
  const [spotType, setSpotType] = useState<string>('car');
  const [spotSection, setSpotSection] = useState('');
  const [spotFloor, setSpotFloor] = useState('');
  const [addingSpots, setAddingSpots] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lotToDelete, setLotToDelete] = useState<ParkingLot | null>(null);
  const [deletingLot, setDeletingLot] = useState(false);

  useEffect(() => {
    setShowCreateModal(false);
    setShowSpotsModal(false);
    setShowDeleteConfirm(false);
    setError(null);
    if (selectedLocation) {
      fetchParkingLots(selectedLocation);
    } else {
      setParkingLots([]);
      setIsLoading(false);
    }
    return () => { parkingRequestRef.current += 1; };
    // Requests are scoped to the header location; CRUD refreshes use the same loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const fetchParkingLots = async (locationId: string) => {
    if (!locationId || locationId !== selectedLocationRef.current) return;
    const request = ++parkingRequestRef.current;
    try {
      setIsLoading(true);
      setError(null);
      console.log('Fetching parking lots for location:', locationId);
      const response = await parkingService.getLots(parseInt(locationId));
      console.log('Parking lots response:', response);
      // Handle both direct array response and wrapped {data: [...]} response
      const lots = Array.isArray(response) ? response : (response.data || []);
      if (request === parkingRequestRef.current) setParkingLots(lots);
    } catch (err: any) {
      if (request !== parkingRequestRef.current) return;
      console.error('Failed to fetch parking lots:', err);
      // Show more details if available
      if (err.response?.data?.detail) {
        console.error('Validation details:', err.response.data.detail);
        setError(`Failed to load parking lots: ${JSON.stringify(err.response.data.detail)}`);
      } else {
        setError('Failed to load parking lots');
      }
      setParkingLots([]);
    } finally {
      if (request === parkingRequestRef.current) setIsLoading(false);
    }
  };

  const handleCreateLot = async () => {
    if (!newLotName.trim() || !selectedLocation) return;
    
    try {
      setCreatingLot(true);
      setError(null);
      
      // Get company_id from the selected location
      const selectedLoc = locations.find(l => l.ycl_id.toString() === selectedLocation);
      const companyId = selectedLoc?.ycl_company || 93; // Default company_id if not found
      
      const payload = {
        company_id: companyId,
        location_id: parseInt(selectedLocation),
        name: newLotName.trim(),
        description: newLotCode.trim() || null,
      };
      
      console.log('Creating parking lot with payload:', payload);
      
      // Call API to create lot
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/v2'}/camera-security/parking/lots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create lot error:', errorData);
        throw new Error(errorData.detail ? JSON.stringify(errorData.detail) : 'Failed to create parking lot');
      }
      
      setSuccessMessage('Parking lot created successfully!');
      setShowCreateModal(false);
      setNewLotName('');
      setNewLotCode('');
      setNewLotType('general');
      fetchParkingLots(selectedLocation);
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create parking lot');
    } finally {
      setCreatingLot(false);
    }
  };

  const handleDeleteLot = async () => {
    if (!lotToDelete) return;
    
    try {
      setDeletingLot(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/v2'}/camera-security/parking/lots/${lotToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete parking lot');
      }
      
      setSuccessMessage('Parking lot deleted successfully!');
      setShowDeleteConfirm(false);
      setLotToDelete(null);
      fetchParkingLots(selectedLocation);
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete parking lot');
    } finally {
      setDeletingLot(false);
    }
  };

  const openSpotsModal = async (lot: ParkingLot) => {
    setSelectedLot(lot);
    setShowSpotsModal(true);
    setLoadingSpots(true);
    
    try {
      const response = await parkingService.getSpots(lot.id);
      console.log('Spots response:', response);
      // Handle both direct array response and wrapped {data: [...]} response
      const spotsData = Array.isArray(response) ? response : (response.data || []);
      setSpots(spotsData);
    } catch (err) {
      console.error('Failed to fetch spots:', err);
      setSpots([]);
    } finally {
      setLoadingSpots(false);
    }
  };

  const handleAddSpots = async () => {
    if (!selectedLot || !spotCount) return;
    
    try {
      setAddingSpots(true);
      
      const spotsToCreate = [];
      const count = parseInt(spotCount);
      const start = parseInt(spotStartNumber) || 1;
      
      for (let i = 0; i < count; i++) {
        const spotNumber = spotPrefix 
          ? `${spotPrefix}${(start + i).toString().padStart(3, '0')}`
          : (start + i).toString();
        
        // Backend expects: spot_number, spot_type, row (optional), etc.
        // Do NOT include lot_id (comes from URL) or status (defaults to available)
        const spotData: Record<string, any> = {
          spot_number: spotNumber,
          spot_type: spotType,
        };
        
        // Map section to row if provided
        if (spotSection) {
          spotData.row = spotSection;
        }
        
        spotsToCreate.push(spotData);
      }
      
      console.log('Creating spots with payload:', JSON.stringify({ spots: spotsToCreate }, null, 2));
      
      // Batch create spots
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/v2'}/camera-security/parking/lots/${selectedLot.id}/spots/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({ spots: spotsToCreate }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Spots creation error:', response.status, errorData);
        throw new Error(errorData?.detail ? JSON.stringify(errorData.detail) : 'Failed to add spots');
      }
      
      setSuccessMessage(`${count} parking spots added successfully!`);
      setShowAddSpotsForm(false);
      setSpotPrefix('');
      setSpotStartNumber('1');
      setSpotCount('10');
      setSpotSection('');
      setSpotFloor('');
      
      // Refresh spots
      const spotsResponse = await parkingService.getSpots(selectedLot.id);
      // Handle both direct array response and wrapped {data: [...]} response
      const newSpotsData = Array.isArray(spotsResponse) ? spotsResponse : (spotsResponse.data || []);
      setSpots(newSpotsData);
      
      // Refresh lots to update counts
      fetchParkingLots(selectedLocation);
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add spots');
    } finally {
      setAddingSpots(false);
    }
  };

  const handleDeleteSpot = async (spotId: number) => {
    if (!selectedLot) return;
    
    // Confirm before deleting
    if (!window.confirm('Are you sure you want to delete this spot?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/v2'}/camera-security/parking/spots/${spotId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Delete spot error:', response.status, errorData);
        throw new Error(errorData?.detail || 'Failed to delete spot');
      }
      
      setSuccessMessage('Spot deleted successfully!');
      
      // Refresh spots - handle both direct array and wrapped response
      const spotsResponse = await parkingService.getSpots(selectedLot.id);
      const spotsData = Array.isArray(spotsResponse) ? spotsResponse : (spotsResponse.data || []);
      setSpots(spotsData);
      
      // Refresh lots to update counts
      fetchParkingLots(selectedLocation);
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete spot');
    }
  };

  const getSpotTypeIcon = (type: string) => {
    switch (type) {
      case 'truck':
      case 'trailer':
        return <Truck size={14} />;
      default:
        return <Car size={14} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge variant="success" size="sm">Available</Badge>;
      case 'occupied':
        return <Badge variant="danger" size="sm">Occupied</Badge>;
      case 'reserved':
        return <Badge variant="warning" size="sm">Reserved</Badge>;
      case 'maintenance':
        return <Badge variant="info" size="sm">Maintenance</Badge>;
      default:
        return <Badge variant="default" size="sm">{status}</Badge>;
    }
  };

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-accent-primary/20 bg-accent-primary/10 text-accent-primary"><ParkingCircle size={21} /></span>
            <div>
              <h1 className="page-title">Parking Management</h1>
              <p className="page-description">Configure parking lots and spots</p>
            </div>
          </div>
          
          {selectedLocation && (
            <Button
              variant="primary"
              leftIcon={<Plus size={18} />}
              onClick={() => setShowCreateModal(true)}
            >
              Add Parking Lot
            </Button>
          )}
        </div>

        {/* Alerts */}
        {error && <Alert variant="error" title="Error" message={error} />}
        {successMessage && <Alert variant="success" title="Success" message={successMessage} />}

        {!selectedLocation && (
          <Card>
            <div className="text-center py-12">
              <MapPin size={40} className="mx-auto text-text-muted mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">Select a Location</h3>
              <p className="text-text-muted">Choose a location in the header to manage its parking lots and spots.</p>
            </div>
          </Card>
        )}

        {/* Parking Lots List */}
        {selectedLocation && (
          <div>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loading size="lg" text="Loading parking lots..." />
              </div>
            ) : parkingLots.length === 0 ? (
              <Card>
                <div className="text-center py-12">
                  <ParkingCircle size={48} className="mx-auto text-text-muted mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">No Parking Lots</h3>
                  <p className="text-text-muted mb-4">
                    Create your first parking lot to start managing parking spaces.
                  </p>
                  <Button
                    variant="primary"
                    leftIcon={<Plus size={18} />}
                    onClick={() => setShowCreateModal(true)}
                  >
                    Create Parking Lot
                  </Button>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {parkingLots.map(lot => (
                  <Card key={lot.id} className="hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary">{lot.name}</h3>
                        {lot.description && (
                          <p className="text-sm text-text-muted">{lot.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/parking/${lot.id}/spots`)}
                          className="p-2 hover:bg-surface-hover rounded-lg transition-colors"
                          title="Manage Spots"
                        >
                          <Edit2 size={16} className="text-text-muted" />
                        </button>
                        <button
                          onClick={() => {
                            setLotToDelete(lot);
                            setShowDeleteConfirm(true);
                          }}
                          className="p-2 hover:bg-status-danger/10 rounded-lg transition-colors"
                          title="Delete Lot"
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {/* Availability Bar */}
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-text-muted">Availability</span>
                          <span className="font-medium text-text-primary">
                            {lot.available_spots} / {lot.total_spots}
                          </span>
                        </div>
                        <div className="w-full bg-surface-hover rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all"
                            style={{ 
                              width: lot.total_spots > 0 
                                ? `${(lot.available_spots / lot.total_spots) * 100}%` 
                                : '0%' 
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-muted">Total Spots</span>
                        <span className="font-medium">{lot.total_spots ?? 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-green-600">Available</span>
                        <span className="font-medium text-green-600">{lot.available_spots ?? 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-red-600">Occupied</span>
                        <span className="font-medium text-red-600">{(lot.total_spots ?? 0) - (lot.available_spots ?? 0)}</span>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-4"
                      onClick={() => navigate(`/parking/${lot.id}/spots`)}
                    >
                      Manage Spots
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Lot Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface-primary rounded-card border border-border-default max-w-md w-full p-6 shadow-xl">
              <h2 className="text-xl font-bold text-text-primary mb-4">Create Parking Lot</h2>
              
              <div className="space-y-4">
                <Input
                  label="Lot Name"
                  placeholder="e.g., Main Parking Lot"
                  value={newLotName}
                  onChange={e => setNewLotName(e.target.value)}
                  required
                />
                
                <Input
                  label="Lot Code (Optional)"
                  placeholder="e.g., LOT-A"
                  value={newLotCode}
                  onChange={e => setNewLotCode(e.target.value)}
                />
                
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Lot Type
                  </label>
                  <select
                    value={newLotType}
                    onChange={e => setNewLotType(e.target.value)}
                    className="w-full px-3 py-2 border border-border-default bg-surface-secondary text-text-primary rounded-lg focus:ring-2 focus:ring-accent-primary"
                  >
                    <option value="general">General</option>
                    <option value="truck">Truck Parking</option>
                    <option value="visitor">Visitor Parking</option>
                    <option value="employee">Employee Parking</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 justify-end mt-6">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleCreateLot}
                  disabled={!newLotName.trim() || creatingLot}
                >
                  {creatingLot ? 'Creating...' : 'Create Lot'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Spots Modal */}
        {showSpotsModal && selectedLot && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface-primary text-text-primary rounded-card border border-border-default max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-text-primary">{selectedLot.name}</h2>
                    <p className="text-sm text-text-muted">Manage parking spots</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<RefreshCw size={16} />}
                      onClick={() => openSpotsModal(selectedLot)}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Plus size={16} />}
                      onClick={() => setShowAddSpotsForm(true)}
                    >
                      Add Spots
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto p-6">
                {/* Add Spots Form */}
                {showAddSpotsForm && (
                  <Card className="mb-6 bg-blue-50 border-blue-200">
                    <h3 className="text-md font-semibold text-text-primary mb-4">Add Multiple Spots</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <Input
                        label="Spot Prefix"
                        placeholder="e.g., A, B, P"
                        value={spotPrefix}
                        onChange={e => setSpotPrefix(e.target.value)}
                      />
                      <Input
                        label="Start Number"
                        type="number"
                        placeholder="1"
                        value={spotStartNumber}
                        onChange={e => setSpotStartNumber(e.target.value)}
                      />
                      <Input
                        label="Number of Spots"
                        type="number"
                        placeholder="10"
                        value={spotCount}
                        onChange={e => setSpotCount(e.target.value)}
                      />
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Spot Type
                        </label>
                        <select
                          value={spotType}
                          onChange={e => setSpotType(e.target.value)}
                          className="w-full px-3 py-2 border border-border-default bg-surface-secondary text-text-primary rounded-lg"
                        >
                          <option value="car">Car</option>
                          <option value="truck">Truck</option>
                          <option value="trailer">Trailer</option>
                          <option value="motorcycle">Motorcycle</option>
                          <option value="handicap">Handicap</option>
                          <option value="ev_charging">EV Charging</option>
                          <option value="reserved">Reserved</option>
                        </select>
                      </div>
                      <Input
                        label="Section (Optional)"
                        placeholder="e.g., North, South"
                        value={spotSection}
                        onChange={e => setSpotSection(e.target.value)}
                      />
                      <Input
                        label="Floor (Optional)"
                        placeholder="e.g., 1, 2, Ground"
                        value={spotFloor}
                        onChange={e => setSpotFloor(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-3 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddSpotsForm(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddSpots}
                        disabled={!spotCount || addingSpots}
                      >
                        {addingSpots ? 'Adding...' : `Add ${spotCount || 0} Spots`}
                      </Button>
                    </div>
                    <p className="text-xs text-text-muted mt-2">
                      Preview: {spotPrefix}{(parseInt(spotStartNumber) || 1).toString().padStart(3, '0')} - {spotPrefix}{((parseInt(spotStartNumber) || 1) + (parseInt(spotCount) || 1) - 1).toString().padStart(3, '0')}
                    </p>
                  </Card>
                )}
                
                {/* Spots List */}
                {loadingSpots ? (
                  <div className="flex justify-center py-8">
                    <Loading text="Loading spots..." />
                  </div>
                ) : spots.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-text-muted">No spots configured. Click "Add Spots" to create parking spots.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {spots.map(spot => {
                      // Handle backend field name variations
                      const spotId = (spot as any).id || spot.spot_id;
                      const spotStatus = spot.status || ((spot as any).is_available ? 'available' : 'occupied');
                      const spotNumber = spot.spot_number || (spot as any).name;
                      
                      return (
                        <div
                          key={spotId}
                          className={`p-3 rounded-lg border relative group flex flex-col items-center ${
                            spotStatus === 'available' 
                              ? 'bg-green-50 border-green-200' 
                              : spotStatus === 'occupied'
                              ? 'bg-red-50 border-red-200'
                              : 'bg-surface-secondary border-border-default'
                          }`}
                        >
                          <button
                            onClick={() => handleDeleteSpot(spotId)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                            title="Delete spot"
                          >
                            ×
                          </button>
                          <div className="flex items-center justify-center gap-1 mb-1">
                            {getSpotTypeIcon(spot.spot_type)}
                            <span className="font-semibold text-sm text-text-primary">{spotNumber}</span>
                          </div>
                          <div className="text-xs text-text-muted mb-2 capitalize">{spot.spot_type}</div>
                          <div className="mt-auto">
                            {getStatusBadge(spotStatus)}
                          </div>
                          {((spot as any).current_vehicle_plate || spot.current_plate) && (
                            <div className="text-xs font-medium text-blue-600 mt-1 truncate w-full text-center bg-blue-50 rounded px-1 py-0.5" 
                                 title={(spot as any).current_vehicle_plate || spot.current_plate}>
                              🚗 {(spot as any).current_vehicle_plate || spot.current_plate}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t bg-surface-secondary">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-text-muted">
                    Total: {spots.length} spots | 
                    Available: {spots.filter(s => s.status === 'available' || (s as any).is_available === true).length} | 
                    Occupied: {spots.filter(s => s.status === 'occupied' || (s as any).is_available === false).length}
                  </div>
                  <Button variant="outline" onClick={() => setShowSpotsModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && lotToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface-primary rounded-card border border-border-default max-w-md w-full p-6 shadow-xl">
              <h2 className="text-xl font-bold text-text-primary mb-4">Delete Parking Lot</h2>
              <p className="text-text-secondary mb-4">
                Are you sure you want to delete <span className="font-semibold">{lotToDelete.name}</span>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium">⚠️ Warning</p>
                <p className="text-sm text-red-700 mt-1">
                  This will delete all {lotToDelete.total_spots} parking spots in this lot. This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={deletingLot}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDeleteLot} disabled={deletingLot}>
                  {deletingLot ? 'Deleting...' : 'Delete Lot'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ParkingLotsPage;
